import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useEffect,
	type ReactNode,
	useRef,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import type { Channel, Workspace } from "@/utils/custom-types";
import { queryClient, trpc } from "@/utils/trpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Route as WorkspaceRoute } from "@/routes/$workspaceId/route";
import { useWorkspaceContext } from "./workspace-context";

export type TabType =
	| "chat"
	| "doc"
	| "board"
	| "meet"
	| "sheet"
	| "storage"
	| "password"
	| "automation"
	| "project"
	| "settings"
	| "more";

export interface Tab {
	id: string;
	channelId: string;
	workspaceId: string;
	name: string;
	type: TabType;
	isPinned: boolean;
	isTemporary: boolean;
	path: string;
	lastVisited: Date;
	panelId?: string; // Panel this tab belongs to
}

// Channel helpers centralized here for reuse across UI
export const getTabTypeFromChannelType = (channelType: string): TabType => {
    const typeMap: Record<string, TabType> = {
        "message-group": "chat",
        dm: "chat",
        "self-dm": "chat",
        doc: "doc",
		sheet: "sheet",
        board: "board",
        meeting: "meet",
        storage: "storage",
        password: "password",
        automation: "automation",
        project: "project",
    };
    return typeMap[channelType] || "chat";
};

export const buildChannelPath = (
    workspaceId: string | number,
    channel: Channel,
): string => {
    const basePathMap: Record<string, string> = {
        "message-group": "chats",
        dm: "chats",
        "self-dm": "chats",
        doc: "docs",
		sheet: "sheets",
        board: "boards",
        meeting: "meets",
        storage: "storage",
        password: "passwords",
        automation: "automations",
        project: "projects",
    };
    const basePath = basePathMap[channel.type] || "chats";
    if (channel.type === "project") {
        return `/${workspaceId}/projects/${channel.id}/chats`;
    }
    return `/${workspaceId}/${basePath}/${channel.id}`;
};

export const createTabDataFromChannel = (
    workspaceId: string | number,
    channel: Channel,
): Omit<Tab, "id" | "lastVisited" | "isTemporary"> => {
    return {
        channelId: String(channel.id),
        workspaceId: String(workspaceId),
        name: channel.title ?? `Channel ${channel.id}`,
        type: getTabTypeFromChannelType(channel.type),
        isPinned: false,
        path: buildChannelPath(workspaceId, channel),
    };
};

export interface TabHistoryEntry {
	tab: Tab;
	closedAt?: Date;
	visitedAt?: Date;
}

export interface TemporaryTabInfo {
	tabId: string;
	panelId: string;
}

export interface WorkspaceTabs {
	version?: number;
	tabs: Tab[];
	activeTabId: string | null; // Global active tab (the one currently navigated to)
	activeTabIdByPanel: Record<string, string | null>; // Per-panel active tab tracking
	temporaryTab: TemporaryTabInfo | null; // Single temporary tab with its panel location
	recentlyVisited: TabHistoryEntry[];
	recentlyClosed: TabHistoryEntry[];
	navigationHistory: string[]; // Array of tab IDs in order of visit
	historyPosition: number; // Current position in navigation history
	focusedTabId: string | null; // Tab currently in focus/full-screen mode
	panelsFocusMode: boolean; // Panels-only focus mode (hides sidebar/toolbar but shows panels)
}

interface TabsContextType {
	tabs: Tab[];
	activeTabId: string | null;
	activeTabIdByPanel: Record<string, string | null>;
	temporaryTab: TemporaryTabInfo | null;
	recentlyVisited: TabHistoryEntry[];
	recentlyClosed: TabHistoryEntry[];
	navigationHistory: string[];
	historyPosition: number;
	canGoBack: boolean;
	canGoForward: boolean;
	focusedTabId: string | null;
	panelsFocusMode: boolean;
	openTab: (
		tab: Omit<Tab, "id" | "lastVisited" | "isTemporary">,
		isTemporary?: boolean,
		activate?: boolean,
		panelId?: string,
		tabId?: string,
	) => void;
	openTemporaryTab: (
		tab: Omit<Tab, "id" | "lastVisited" | "isTemporary">,
		panelId?: string,
	) => void;
	openPermanentTab: (
		tab: Omit<Tab, "id" | "lastVisited" | "isTemporary">,
		panelId?: string,
	) => void;
	openTabInBackground: (
		tab: Omit<Tab, "id" | "lastVisited" | "isTemporary">,
		panelId?: string,
	) => void;
	confirmTab: (tabId: string) => void;
	closeTabs: (tabsToCloseInput: Tab[]) => void;
	closeTab: (tabId: string) => void;
	setActiveTab: (tabId: string, updateHistory?: boolean) => void;
	setActiveTabForPanel: (panelId: string, tabId: string | null) => void;
	getActiveTabForPanel: (panelId: string) => string | null;
	togglePinTab: (tabId: string) => void;
	restoreTab: (tab: Tab) => void;
	clearHistory: () => void;
	getTabByPath: (path: string) => Tab | undefined;
	getTabsByPanel: (panelId: string) => Tab[];
	setCurrentWorkspace: (workspaceId: string) => void;
	navigateBack: () => void;
	navigateForward: () => void;
	reorderTabs: (oldIndex: number, newIndex: number, panelId?: string) => void;
	moveTabToPanel: (tabId: string, targetPanelId: string) => void;
	moveTabToPanelLastIndex: (tabId: string, targetPanelId: string) => void;
	setFocusedTab: (tabId: string) => void;
	clearFocusedTab: () => void;
	setPanelsFocusMode: (enabled: boolean) => void;
	updateTabNameForChannel: (channelId: string, newName: string) => void;
	// Channel helper functions
	getTabTypeFromChannelType: (channelType: string) => TabType;
	buildChannelPath: (workspaceId: string | number, channel: Channel) => string;
	createTabDataFromChannel: (
		workspaceId: string | number,
		channel: Channel,
	) => Omit<Tab, "id" | "lastVisited" | "isTemporary">;
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

const MAX_TABS = 15;
const MAX_HISTORY = 10;
const STORAGE_KEY = "workspace-tabs";

// Helper function to get workspace-specific storage key
const getWorkspaceStorageKey = (workspaceId: string) =>
    `${STORAGE_KEY}-${workspaceId}`;

// Helper function to get empty workspace tabs
const getEmptyWorkspaceTabs = (): WorkspaceTabs => ({
	version: 0,
	tabs: [],
	activeTabId: null,
	activeTabIdByPanel: {},
	temporaryTab: null,
	recentlyVisited: [],
	recentlyClosed: [],
	navigationHistory: [],
	historyPosition: -1,
	focusedTabId: null,
	panelsFocusMode: false,
});

export const TabsProvider: React.FC<{ 
	children: ReactNode;	
}> = ({
    children,
}) => {
	const navigate = useNavigate();

	const { currentWorkspace } = WorkspaceRoute.useLoaderData();

	const { tabState, tabStateLoading } = useWorkspaceContext();

	const hasSavedTabsRef = useRef(false);
	const tabVersionRef = useRef<number>(0);

	const currentWorkspaceId = currentWorkspace.id;

	const tabStateQueryKey = trpc.me.getTabState.queryKey({
		workspaceId: Number(currentWorkspaceId),
	});

	const updateTabStateMutation = useMutation(
		trpc.me.updateTabState.mutationOptions({
			onSuccess: () => {
				// queryClient.invalidateQueries({ queryKey: tabStateQueryKey });
			},
		}),
	);

	const updateTabStateMutationWebSocket = useMutation(
		trpc.me.updateTabStateWebSocket.mutationOptions({
			onSuccess: () => {
				// queryClient.invalidateQueries({ queryKey: tabStateQueryKey });
			},
		}),
	);

	const pendingWorkspaceTabsRef = useRef<WorkspaceTabs | null>(null);

	const flushTimerRef = useRef<number | null>(null);

	const DEBOUNCE_MS = 2000;

	function flushTabState() {
		if (!pendingWorkspaceTabsRef.current) return;

		const tabStateToFlush = pendingWorkspaceTabsRef.current;
		pendingWorkspaceTabsRef.current = null;

		// console.log(`updating ${currentWorkspaceId} with `, tabStateToFlush);

		updateTabStateMutation.mutate({
			workspaceId: Number(currentWorkspaceId),
			tabState: tabStateToFlush
		})
	}

	function scheduleTabStateFlush() {
		if (flushTimerRef.current) window.clearTimeout(flushTimerRef.current);
		flushTimerRef.current = window.setTimeout(() => {
			flushTimerRef.current = null;
			flushTabState();
		}, DEBOUNCE_MS);
	}

	function queueTabStateSave(tabs: WorkspaceTabs) {
		pendingWorkspaceTabsRef.current = tabs;
		scheduleTabStateFlush();
	}

	// useEffect(() => {
	// 	window.addEventListener("beforeunload", flushTabState);
		
	// 	return () => {
	// 		window.removeEventListener("beforeunload", flushTabState);
	// 	};
	// }, [currentWorkspaceId]);

	const loadWorkspaceTabs = (workspaceId: number): WorkspaceTabs => {
		try {
			// Use 'any' for stored data to handle backwards compatibility with old format
			const stored: any = tabState;

			if (stored) {
				const tabs = stored.tabs?.map((t: any) => ({
					...t,
					lastVisited: new Date(t.lastVisited),
					isTemporary: t.isTemporary || false, // Default to false for existing tabs
				})) || [];

				// Build activeTabIdByPanel from tabs if not present in stored state
				// This provides backwards compatibility
				let activeTabIdByPanel: Record<string, string | null> = stored.activeTabIdByPanel || {};
				if (Object.keys(activeTabIdByPanel).length === 0 && tabs.length > 0) {
					// Initialize from tabs - set first tab of each panel as active
					const panelIds = new Set<string>(tabs.map((t: Tab) => t.panelId || "default"));
					for (const panelId of panelIds) {
						const panelTabs = tabs.filter((t: Tab) => (t.panelId || "default") === panelId);
						if (panelTabs.length > 0) {
							activeTabIdByPanel[panelId] = panelTabs[0].id;
						}
					}
				}

				// Handle backwards compatibility for temporaryTab
				// Old format: temporaryTabId: string | null
				// New format: temporaryTab: { tabId: string; panelId: string } | null
				let temporaryTab: TemporaryTabInfo | null = null;
				if (stored.temporaryTab) {
					temporaryTab = stored.temporaryTab;
				} else if (stored.temporaryTabId) {
					// Migrate from old format
					const tempTab = tabs.find((t: Tab) => t.id === stored.temporaryTabId);
					if (tempTab) {
						temporaryTab = {
							tabId: stored.temporaryTabId,
							panelId: tempTab.panelId || "default",
						};
					}
				}

				return {
					version: stored.version ?? 0,
					tabs,
					activeTabId: stored.activeTabId || null,
					activeTabIdByPanel,
					temporaryTab,
					recentlyVisited:
						stored.recentlyVisited?.map((entry: any) => ({
							...entry,
							tab: {
								...entry.tab,
								lastVisited: new Date(entry.tab.lastVisited),
								isTemporary: entry.tab.isTemporary || false,
							},
							visitedAt: entry.visitedAt
								? new Date(entry.visitedAt)
								: undefined,
						})) || [],
					recentlyClosed:
						stored.recentlyClosed?.map((entry: any) => ({
							...entry,
							tab: {
								...entry.tab,
								lastVisited: new Date(entry.tab.lastVisited),
								isTemporary: entry.tab.isTemporary || false,
							},
							closedAt: entry.closedAt ? new Date(entry.closedAt) : undefined,
						})) || [],
					navigationHistory: stored.navigationHistory || [],
					historyPosition: stored.historyPosition ?? -1,
					focusedTabId: stored.focusedTabId || null,
					panelsFocusMode: stored.panelsFocusMode || false,
				};
			}
		} catch (e) {
			console.error(`Failed to load tabs for workspace ${workspaceId}:`, e);
		}
		return getEmptyWorkspaceTabs();
	};

	useEffect(() => {
		hasSavedTabsRef.current = false;
	}, [currentWorkspaceId]);

	// Initialize workspace tabs
	const [workspaceTabsData, setWorkspaceTabsData] = useState<WorkspaceTabs>(
		() => {
			return currentWorkspaceId
				? loadWorkspaceTabs(currentWorkspaceId)
				: getEmptyWorkspaceTabs();
		},
	);

	useEffect(() => {
		if (tabStateLoading) {
			return;
		}

		const loadedTabs = loadWorkspaceTabs(currentWorkspaceId);
		// console.log(`[TabsContext][workspace: ${currentWorkspaceId}] got tabState :`, JSON.stringify(loadedTabs, null, 2));

		tabVersionRef.current = loadedTabs.version ?? 0;

		setWorkspaceTabsData(loadedTabs);
	}, [currentWorkspaceId, tabStateLoading]);

	// Derived state for current workspace
	const tabs = workspaceTabsData.tabs;
	const activeTabId = workspaceTabsData.activeTabId;
	const activeTabIdByPanel = workspaceTabsData.activeTabIdByPanel;
	const temporaryTab = workspaceTabsData.temporaryTab;
	const recentlyVisited = workspaceTabsData.recentlyVisited;
	const recentlyClosed = workspaceTabsData.recentlyClosed;
	const navigationHistory = workspaceTabsData.navigationHistory;
	const historyPosition = workspaceTabsData.historyPosition;
	const focusedTabId = workspaceTabsData.focusedTabId;
	const panelsFocusMode = workspaceTabsData.panelsFocusMode;
	const canGoBack = historyPosition > 0;
	const canGoForward = historyPosition < navigationHistory.length - 1;

	function tabsEqual(a: WorkspaceTabs, b: WorkspaceTabs) {
		// Good enough for this use case; you can optimize later if needed
		return JSON.stringify(a) === JSON.stringify(b);
	}

	useEffect(() => {
		if (tabStateLoading) {
			return;
		}
		if (!hasSavedTabsRef.current) {
			hasSavedTabsRef.current = true;
			return;
		}

		const permanentTabs = tabs.filter((tab) => !tab.isTemporary);
		const nextVersion = (tabVersionRef.current ?? 0) + 1;
  		tabVersionRef.current = nextVersion;
		const data = {
			version: nextVersion,
			tabs: permanentTabs,
			activeTabId: permanentTabs.find((tab) => tab.id === activeTabId)
				? activeTabId
				: null,
			activeTabIdByPanel: activeTabIdByPanel,
			temporaryTab: null, // Don't persist temporary tabs
			recentlyVisited: recentlyVisited.slice(0, MAX_HISTORY),
			recentlyClosed: recentlyClosed.slice(0, MAX_HISTORY),
			navigationHistory: navigationHistory,
			historyPosition: historyPosition,
			focusedTabId: focusedTabId,
			panelsFocusMode: panelsFocusMode,
		};

		// console.log(`[TabsContext][workspace: ${currentWorkspaceId}] mutating tabState :`, JSON.stringify(data, null, 2));

		updateTabStateMutationWebSocket.mutate({
			workspaceId: Number(currentWorkspaceId),
			tabState: data
		})
	}, [
		tabs,
		activeTabId,
		activeTabIdByPanel,
		temporaryTab,
		recentlyVisited,
		recentlyClosed,
		navigationHistory,
		historyPosition,
		focusedTabId,
		panelsFocusMode,
		currentWorkspaceId,
		tabStateLoading,
	]);

	const generateTabId = () =>
		`tab-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

	const generateTabIdV2 = (channelId: string, workspaceId: string) =>
		`tab-${channelId}-${workspaceId}`;

	const generateTabIdTest = (channelId: string, workspaceId: string, tabname: string) =>
		`${tabname}-tab-${channelId}-${workspaceId}`;

	const openTab = useCallback(
		(
			tabData: Omit<Tab, "id" | "lastVisited" | "isTemporary">,
			isTemporary = false,
			activate = true,
			panelId?: string,
			tabId?: string,
		) => {
			const targetPanelId = panelId || "default";
			
			// Check if tab already exists (channel already open somewhere)
			const existingTab = tabs.find((t) => t.path === tabData.path);

			if (existingTab) {
				// If existing tab is temporary and we're trying to open as permanent, convert it
				if (existingTab.isTemporary && !isTemporary) {
					confirmTab(existingTab.id);
				}

				// Only activate and navigate if requested
				if (activate) {
					setWorkspaceTabsData((prev) => {
						// Update navigation history
						let newHistory = [...prev.navigationHistory];
						let newPosition = prev.historyPosition;

						const filteredVisited = prev.recentlyVisited.filter(
							(entry) => entry.tab.id !== existingTab.id,
						);
						
						const newRecentlyVisited = [
							{ tab: existingTab, visitedAt: new Date() },
							...filteredVisited.slice(0, MAX_HISTORY - 1),
						];		

						// If we're not at the end of history, truncate forward history
						if (newPosition < newHistory.length - 1) {
							newHistory = newHistory.slice(0, newPosition + 1);
						}

						newHistory = newHistory.filter((id) => id !== existingTab.id);
						newHistory.push(existingTab.id);
						newPosition = newHistory.length - 1;

						// Update activeTabIdByPanel for the panel this tab belongs to
						const tabPanelId = existingTab.panelId || "default";
						const newActiveTabIdByPanel = {
							...prev.activeTabIdByPanel,
							[tabPanelId]: existingTab.id,
						};
						
						return {
							...prev,
							activeTabId: existingTab.id,
							activeTabIdByPanel: newActiveTabIdByPanel,
							navigationHistory: newHistory,
							historyPosition: newPosition,
							recentlyVisited: newRecentlyVisited
						};
					});
					navigate({ to: existingTab.path as any });
				}
				return;
			}

			// Create new tab
			const newTab: Tab = {
				...tabData,
				id: tabId ? tabId : generateTabIdV2(tabData.channelId, tabData.workspaceId),
				lastVisited: new Date(),
				isTemporary,
				panelId: targetPanelId,
			};

			setWorkspaceTabsData((prev) => {
				let newTabs = [...prev.tabs];
				let newTemporaryTab = prev.temporaryTab;
				let newRecentlyClosed = prev.recentlyClosed;

				const filteredVisited = prev.recentlyVisited.filter(
					(entry) => entry.tab.id !== newTab.id,
				);
				
				const newRecentlyVisited = [
					{ tab: newTab, visitedAt: new Date() },
					...filteredVisited.slice(0, MAX_HISTORY - 1),
				];

				// Update navigation history if activating
				let newHistory = [...prev.navigationHistory];
				let newPosition = prev.historyPosition;

				if (activate && prev.activeTabId !== newTab.id) {
					// If we're not at the end of history, truncate forward history
					if (newPosition < newHistory.length - 1) {
						newHistory = newHistory.slice(0, newPosition + 1);
					}

					newHistory = newHistory.filter((id) => id !== tabId);

					// Add to history
					newHistory.push(newTab.id);
					newPosition = newHistory.length - 1;
				}

				if (isTemporary) {
					// If opening a temporary tab, remove the existing temporary tab (from any panel)
					if (prev.temporaryTab) {
						const oldTempTabIndex = newTabs.findIndex(
							(t) => t.id === prev.temporaryTab?.tabId,
						);
						if (oldTempTabIndex !== -1) {
							// Remove the old temporary tab entirely
							newTabs.splice(oldTempTabIndex, 1);
						}
					}
					// Add the new temporary tab to the target panel
					newTabs.push(newTab);
					newTemporaryTab = { tabId: newTab.id, panelId: targetPanelId };
				} else {
					// Opening a permanent tab
					newTabs.push(newTab);

					// If we exceed max tabs, close the oldest unpinned tab
					if (newTabs.length > MAX_TABS) {
						const unpinnedTabs = newTabs.filter(
							(t) => !t.isPinned && !t.isTemporary,
						);
						if (unpinnedTabs.length > 0) {
							const oldestUnpinned = unpinnedTabs.reduce((oldest, current) =>
								current.lastVisited < oldest.lastVisited ? current : oldest,
							);

							// Add to recently closed
							newRecentlyClosed = [
								{ tab: oldestUnpinned, closedAt: new Date() },
								...prev.recentlyClosed.slice(0, MAX_HISTORY - 1),
							];

							newTabs = newTabs.filter((t) => t.id !== oldestUnpinned.id);
						}
					}
				}

				// Update activeTabIdByPanel
				const newActiveTabIdByPanel = { ...prev.activeTabIdByPanel };
				if (activate) {
					newActiveTabIdByPanel[targetPanelId] = newTab.id;
				}

				return {
					...prev,
					tabs: newTabs,
					activeTabId: activate ? newTab.id : prev.activeTabId,
					activeTabIdByPanel: newActiveTabIdByPanel,
					temporaryTab: newTemporaryTab,
					recentlyClosed: newRecentlyClosed,
					recentlyVisited: newRecentlyVisited,
					navigationHistory: newHistory,
					historyPosition: newPosition
				};
			});
			
			// Only navigate if we're activating the tab
			if (activate) {
				navigate({ to: newTab.path as any });
			}
		},
		[tabs, navigate],
	);

	const openTemporaryTab = useCallback(
		(tabData: Omit<Tab, "id" | "lastVisited" | "isTemporary">, panelId?: string) => {
			openTab(tabData, true, true, panelId);
		},
		[openTab],
	);

	const openPermanentTab = useCallback(
		(tabData: Omit<Tab, "id" | "lastVisited" | "isTemporary">, panelId?: string) => {
			openTab(tabData, false, true, panelId);
		},
		[openTab],
	);

	const openTabInBackground = useCallback(
		(tabData: Omit<Tab, "id" | "lastVisited" | "isTemporary">, panelId?: string) => {
			openTab(tabData, false, false, panelId);
		},
		[openTab],
	);

	const confirmTab = useCallback((tabId: string) => {
		setWorkspaceTabsData((prev) => {
			const updatedTabs = prev.tabs.map((tab) =>
				tab.id === tabId ? { ...tab, isTemporary: false } : tab,
			);

			return {
				...prev,
				tabs: updatedTabs,
				temporaryTab:
					prev.temporaryTab?.tabId === tabId ? null : prev.temporaryTab,
			};
		});
	}, []);

	const closeTabs = useCallback(
		(tabsToCloseInput: Tab[]) => {
			setWorkspaceTabsData((prev) => {
				const idsRequested = new Set(tabsToCloseInput.map((t) => t.id));
	  
				// Resolve from prev (source of truth) + skip pinned
				const tabsToClose = prev.tabs.filter(
					(t) => idsRequested.has(t.id) && !t.isPinned,
				);
		
				if (tabsToClose.length === 0) return prev;
		
				const closedIds = new Set(tabsToClose.map((t) => t.id));
		
				let remainingTabs = prev.tabs.filter((t) => !closedIds.has(t.id));
				let newActiveTabId = prev.activeTabId;
				let newFocusedTabId = prev.focusedTabId;
				let newTemporaryTab = prev.temporaryTab;
				const closedTabPanelId = tabsToCloseInput[0].panelId ?? "default";
				const hasTabsRemainingInPanel = remainingTabs.some(
					(tab) => (tab.panelId ?? "default") === closedTabPanelId,
				);

				// If closing temporary tab, clear temporaryTab
				if (prev.temporaryTab && closedIds.has(prev.temporaryTab.tabId)) {
					newTemporaryTab = null;
				}

				let newRecentlyVisited;

				// Update activeTabIdByPanel - remove closed tabs from panel tracking
				const newActiveTabIdByPanel = { ...prev.activeTabIdByPanel };
				
				// If closing active tab, switch to another
				if (prev.activeTabId && closedIds.has(prev.activeTabId)) {
					if (remainingTabs.length > 0) {
						// Try to find a tab in the same panel first
						const panelTabs = remainingTabs.filter(
							(t) => (t.panelId ?? "default") === closedTabPanelId
						);
						const nextTab = panelTabs.length > 0 
							? panelTabs[panelTabs.length - 1]
							: remainingTabs[remainingTabs.length - 1];
						newActiveTabId = nextTab.id;

						if (prev.focusedTabId && closedIds.has(prev.focusedTabId)) {
							newFocusedTabId = hasTabsRemainingInPanel ? nextTab.id : null;
						}

						remainingTabs = remainingTabs.map((t) =>
							t.id === newActiveTabId ? { ...t, lastVisited: new Date() } : t,
						);
		
						const filteredVisited = prev.recentlyVisited.filter(
							(entry) => entry.tab.id !== newActiveTabId,
						);
		
						newRecentlyVisited = [
							{ tab: nextTab, visitedAt: new Date() },
							...filteredVisited.slice(0, MAX_HISTORY - 1),
						];

						// Update panel active tab
						const nextTabPanelId = nextTab.panelId ?? "default";
						newActiveTabIdByPanel[nextTabPanelId] = nextTab.id;

						navigate({ to: nextTab.path as any });
					} else {
						newFocusedTabId = null;
						newActiveTabId = null;
					}
				}

				// Clean up activeTabIdByPanel for panels that lost their active tab
				for (const [pId, tabId] of Object.entries(newActiveTabIdByPanel)) {
					if (tabId && closedIds.has(tabId)) {
						const panelRemainingTabs = remainingTabs.filter(
							(t) => (t.panelId ?? "default") === pId
						);
						if (panelRemainingTabs.length > 0) {
							newActiveTabIdByPanel[pId] = panelRemainingTabs[panelRemainingTabs.length - 1].id;
						} else {
							newActiveTabIdByPanel[pId] = null;
						}
					}
				}

				if (prev.focusedTabId && closedIds.has(prev.focusedTabId) && !hasTabsRemainingInPanel) {
					newFocusedTabId = null;
				}

				const oldHistory = prev.navigationHistory;
				const oldPos = prev.historyPosition;

				let removedBeforeOrAt = 0;
				for (let i = 0; i < oldHistory.length; i++) {
					if (i <= oldPos && closedIds.has(oldHistory[i])) {
						removedBeforeOrAt++;
					}
				}

				const newNavigationHistory = oldHistory.filter((id) => !closedIds.has(id));

				// Shift cursor left by number removed before/at
				let newHistoryPosition = oldPos - removedBeforeOrAt;

				// Clamp to valid range (-1 means "no history / nothing selected")
				newHistoryPosition = Math.max(
					-1,
					Math.min(newHistoryPosition, newNavigationHistory.length - 1),
				);

				const recentlyClosedTabs = prev.recentlyClosed.filter((entry) => !closedIds.has(entry.tab.id))

				const closedEntries = tabsToClose.map((tab) => ({
					tab,
					closedAt: new Date(),
				}));

				const newRecentlyClosed = [
					...closedEntries,
					...recentlyClosedTabs,
				].slice(0, MAX_HISTORY);

				return {
					...prev,
					tabs: remainingTabs,
					activeTabId: newActiveTabId,
					activeTabIdByPanel: newActiveTabIdByPanel,
					temporaryTab: newTemporaryTab,
					focusedTabId: newFocusedTabId,
					navigationHistory: newNavigationHistory,
					historyPosition: newHistoryPosition,
					recentlyVisited: newRecentlyVisited ? newRecentlyVisited : prev.recentlyVisited,
					recentlyClosed: newRecentlyClosed,
				};
			});
		},
		[tabs, activeTabId, navigate],
	);
	
	const closeTab = useCallback(
		(tabId: string) => {
			const tabToClose = tabs.find((t) => t.id === tabId);

			if (!tabToClose || tabToClose.isPinned) return;

			setWorkspaceTabsData((prev) => {
				let remainingTabs = prev.tabs.filter((t) => t.id !== tabId);
				let newActiveTabId = prev.activeTabId;
				let newFocusedTabId = prev.focusedTabId;
				let newTemporaryTab = prev.temporaryTab;
				const closedTabPanelId = tabToClose.panelId ?? "default";
				const hasTabsRemainingInPanel = remainingTabs.some(
					(tab) => (tab.panelId ?? "default") === closedTabPanelId,
				);

				// If closing temporary tab, clear temporaryTab
				if (prev.temporaryTab?.tabId === tabId) {
					newTemporaryTab = null;
				}

				let newRecentlyVisited;
				const newActiveTabIdByPanel = { ...prev.activeTabIdByPanel };

				// If closing active tab, switch to another
				if (prev.activeTabId === tabId) {
					if (remainingTabs.length > 0) {
						// Try to find a tab in the same panel first
						const panelTabs = remainingTabs.filter(
							(t) => (t.panelId ?? "default") === closedTabPanelId
						);
						const nextTab = panelTabs.length > 0 
							? panelTabs[panelTabs.length - 1]
							: remainingTabs[remainingTabs.length - 1];
						newActiveTabId = nextTab.id;

						if (tabId === focusedTabId) {
							newFocusedTabId = hasTabsRemainingInPanel ? nextTab.id : null;
						}

						remainingTabs = remainingTabs.map((t) =>
							t.id === newActiveTabId ? { ...t, lastVisited: new Date() } : t,
						);
		
						const filteredVisited = prev.recentlyVisited.filter(
							(entry) => entry.tab.id !== newActiveTabId,
						);
		
						newRecentlyVisited = [
							{ tab: nextTab, visitedAt: new Date() },
							...filteredVisited.slice(0, MAX_HISTORY - 1),
						];

						// Update panel active tab
						const nextTabPanelId = nextTab.panelId ?? "default";
						newActiveTabIdByPanel[nextTabPanelId] = nextTab.id;

						navigate({ to: nextTab.path as any });
					} else {
						newFocusedTabId = null;
						newActiveTabId = null;
					}
				}

				// Update activeTabIdByPanel for the closed tab's panel
				if (newActiveTabIdByPanel[closedTabPanelId] === tabId) {
					const panelRemainingTabs = remainingTabs.filter(
						(t) => (t.panelId ?? "default") === closedTabPanelId
					);
					if (panelRemainingTabs.length > 0) {
						newActiveTabIdByPanel[closedTabPanelId] = panelRemainingTabs[panelRemainingTabs.length - 1].id;
					} else {
						newActiveTabIdByPanel[closedTabPanelId] = null;
					}
				}

				if (prev.focusedTabId === tabId && !hasTabsRemainingInPanel) {
					newFocusedTabId = null;
				}

				const oldHistory = prev.navigationHistory;
				const removedIndex = oldHistory.indexOf(tabToClose.id);

				const newNavigationHistory = prev.navigationHistory.filter((id) => id !== tabToClose.id)
				let newHistoryPosition = prev.historyPosition;

				if (prev.historyPosition >= removedIndex && removedIndex !== -1) {
					newHistoryPosition--;
				}

				const recentlyClosedTabs = prev.recentlyClosed.filter((entry) => entry.tab.id !== tabToClose.id)

				return {
					...prev,
					tabs: remainingTabs,
					activeTabId: newActiveTabId,
					activeTabIdByPanel: newActiveTabIdByPanel,
					temporaryTab: newTemporaryTab,
					focusedTabId: newFocusedTabId,
					navigationHistory: newNavigationHistory,
					historyPosition: newHistoryPosition,
					recentlyVisited: newRecentlyVisited ? newRecentlyVisited : prev.recentlyVisited,
					recentlyClosed: [
						{ tab: tabToClose, closedAt: new Date() },
						...recentlyClosedTabs.slice(0, MAX_HISTORY - 1),
					],
				};
			});
		},
		[tabs, activeTabId, navigate],
	);

	const setActiveTab = useCallback(
		(tabId: string, updateHistory = true) => {
			setWorkspaceTabsData((prev) => {
				const tab = prev.tabs.find((t) => t.id === tabId);
				if (!tab) return prev;

				const updatedTabs = prev.tabs.map((t) =>
					t.id === tabId ? { ...t, lastVisited: new Date() } : t,
				);

				const filteredVisited = prev.recentlyVisited.filter(
					(entry) => entry.tab.id !== tabId,
				);
				const newRecentlyVisited = [
					{ tab, visitedAt: new Date() },
					...filteredVisited.slice(0, MAX_HISTORY - 1),
				];

				// Update navigation history if requested
				let newHistory = [...prev.navigationHistory];
				let newPosition = prev.historyPosition;

				if (updateHistory) {
					// Don't add to history if it's already the current active tab
					if (prev.activeTabId !== tabId) {
						// If we're not at the end of history, truncate forward history
						if (newPosition < newHistory.length - 1) {
							newHistory = newHistory.slice(0, newPosition + 1);
						}

						newHistory = newHistory.filter((id) => id !== tabId);

						// Add to history
						newHistory.push(tabId);
						newPosition = newHistory.length - 1;
					}
				}

				// Update activeTabIdByPanel
				const tabPanelId = tab.panelId || "default";
				const newActiveTabIdByPanel = {
					...prev.activeTabIdByPanel,
					[tabPanelId]: tabId,
				};

				return {
					...prev,
					tabs: updatedTabs,
					activeTabId: tabId,
					activeTabIdByPanel: newActiveTabIdByPanel,
					recentlyVisited: newRecentlyVisited,
					navigationHistory: newHistory,
					historyPosition: newPosition,
				};
			});
		},
		[tabs],
	);

	const setActiveTabForPanel = useCallback(
		(panelId: string, tabId: string | null) => {
			setWorkspaceTabsData((prev) => ({
				...prev,
				activeTabIdByPanel: {
					...prev.activeTabIdByPanel,
					[panelId]: tabId,
				},
			}));
		},
		[],
	);

	const getActiveTabForPanel = useCallback(
		(panelId: string): string | null => {
			return activeTabIdByPanel[panelId] ?? null;
		},
		[activeTabIdByPanel],
	);

	const togglePinTab = useCallback((tabId: string) => {
		setWorkspaceTabsData((prev) => ({
			...prev,
			tabs: prev.tabs.map((tab) =>
				tab.id === tabId ? { ...tab, isPinned: !tab.isPinned } : tab,
			),
		}));
	}, []);

	const restoreTab = useCallback(
		(tab: Tab) => {
			// Remove from recently closed
			setWorkspaceTabsData((prev) => ({
				...prev,
				recentlyClosed: prev.recentlyClosed.filter(
					(entry) => entry.tab.id !== tab.id,
				),
			}));

			// Open the tab as permanent (restored tabs should not be temporary)
			openTab(
				{
					channelId: tab.channelId,
					workspaceId: tab.workspaceId,
					name: tab.name,
					type: tab.type,
					isPinned: false,
					path: tab.path,
				},
				false,
				true,
				undefined,
				tab.id
			);
		},
		[openTab],
	);

	const clearHistory = useCallback(() => {
		setWorkspaceTabsData((prev) => ({
			...prev,
			recentlyClosed: [],
			recentlyVisited: [],
			navigationHistory: [],
			historyPosition: -1
		}));
	}, []);

	const getTabByPath = useCallback(
		(path: string) => {
			return tabs.find((t) => t.path === path);
		},
		[tabs],
	);

	const getTabsByPanel = useCallback(
		(panelId: string) => {
			// Support both specific panel IDs and default panel
			return tabs.filter((t) => (t.panelId || "default") === panelId);
		},
		[tabs],
	);

	const moveTabToPanel = useCallback(
		(tabId: string, targetPanelId: string) => {
			setWorkspaceTabsData((prev) => ({
				...prev,
				tabs: prev.tabs.map((tab) =>
					tab.id === tabId ? { ...tab, panelId: targetPanelId } : tab,
				),
			}));
		},
		[],
	);

	const moveTabToPanelLastIndex = useCallback(
		(tabId: string, targetPanelId: string) => {
			setWorkspaceTabsData((prev) => {
				const tabs = prev.tabs.slice();
				const fromIdx = tabs.findIndex(t => t.id === tabId);
				if (fromIdx === -1) return prev;

				const target = targetPanelId || "default";
				
				const moved = { ...tabs[fromIdx], panelId: targetPanelId };
				tabs.splice(fromIdx, 1);

				let lastTargetIdx = -1;
				for (let i = 0; i < tabs.length; i++) {
					if ((tabs[i].panelId || "default") === target) lastTargetIdx = i;
				}

				const insertAt = lastTargetIdx >= 0 ? lastTargetIdx + 1 : tabs.length;
				tabs.splice(insertAt, 0, moved);

				return { ...prev, tabs };
			});
		},
		[],
	);

	const navigateBack = useCallback(() => {
		if (!canGoBack) return;

		const newPosition = historyPosition - 1;
		const targetTabId = navigationHistory[newPosition];
		const targetTab = tabs.find((t) => t.id === targetTabId);

		if (targetTab) {
			setWorkspaceTabsData((prev) => {
				const updatedTabs = prev.tabs.map((t) =>
					t.id === targetTabId ? { ...t, lastVisited: new Date() } : t,
				);

				const filteredVisited = prev.recentlyVisited.filter(
					(entry) => entry.tab.id !== targetTabId,
				);

				const newRecentlyVisited = [
					{ tab: targetTab, visitedAt: new Date() },
					...filteredVisited.slice(0, MAX_HISTORY - 1),
				];

				// console.log(`[navigateBack] at position ${newPosition} = ${prev.navigationHistory[newPosition]}`);

				return {
					...prev,
					tabs: updatedTabs,
					historyPosition: newPosition,
					recentlyVisited: newRecentlyVisited,
					activeTabId: targetTabId,
				}
			});
			// Navigate without updating history since we're moving through existing history
			navigate({ to: targetTab.path as any });
		} else {
			// If tab no longer exists, remove it from history and try again
			setWorkspaceTabsData((prev) => {
				const newHistory = [...prev.navigationHistory];
				newHistory.splice(newPosition, 1);

				// console.log("[navigateBack] newHistory: ", JSON.stringify(newHistory, null, 2));
				// console.log(`[navigateBack] at position ${newPosition} = ${newHistory[newPosition]}`);

				return {
					...prev,
					navigationHistory: newHistory,
					historyPosition: Math.min(newPosition, newHistory.length - 1),
				};
			});
		}
	}, [canGoBack, historyPosition, navigationHistory, tabs, navigate]);

	const navigateForward = useCallback(() => {
		if (!canGoForward) return;

		const newPosition = historyPosition + 1;
		const targetTabId = navigationHistory[newPosition];
		const targetTab = tabs.find((t) => t.id === targetTabId);

		if (targetTab) {
			setWorkspaceTabsData((prev) => {
				const updatedTabs = prev.tabs.map((t) =>
					t.id === targetTabId ? { ...t, lastVisited: new Date() } : t,
				);

				const filteredVisited = prev.recentlyVisited.filter(
					(entry) => entry.tab.id !== targetTabId,
				);

				const newRecentlyVisited = [
					{ tab: targetTab, visitedAt: new Date() },
					...filteredVisited.slice(0, MAX_HISTORY - 1),
				];

				// console.log(`[navigateForward] at position ${newPosition} = ${prev.navigationHistory[newPosition]}`);

				return {
					...prev,
					tabs: updatedTabs,
					historyPosition: newPosition,
					activeTabId: targetTabId,
					recentlyVisited: newRecentlyVisited
				}
			});
			// Navigate without updating history since we're moving through existing history
			navigate({ to: targetTab.path as any });
		} else {
			// If tab no longer exists, remove it from history and try again
			setWorkspaceTabsData((prev) => {
				const newHistory = [...prev.navigationHistory];
				newHistory.splice(newPosition, 1);
				// console.log("[navigateForward] newHistory: ", JSON.stringify(newHistory, null, 2));
				// console.log(`[navigateForward] at position ${newPosition} = ${newHistory[newPosition]}`);

				return {
					...prev,
					navigationHistory: newHistory,
					historyPosition: Math.min(prev.historyPosition, newHistory.length - 1),
				};
			});
		}
	}, [canGoForward, historyPosition, navigationHistory, tabs, navigate]);

	const reorderTabs = useCallback((oldIndex: number, newIndex: number, panelId?: string) => {
		setWorkspaceTabsData((prev) => {
			let tabsToReorder = prev.tabs;
			
			// If panelId is provided, filter tabs for that panel
			if (panelId) {
				tabsToReorder = prev.tabs.filter(tab => tab.panelId === panelId);
			}
			
			// Separate pinned and unpinned tabs
			const pinnedTabs = tabsToReorder.filter(tab => tab.isPinned);
			const unpinnedTabs = tabsToReorder.filter(tab => !tab.isPinned);
			
			// Check if we're moving a pinned tab or unpinned tab
			const isMovingPinnedTab = oldIndex < pinnedTabs.length;
			const isMovingToPinnedArea = newIndex < pinnedTabs.length;
			
			if (isMovingPinnedTab || isMovingToPinnedArea) {
				// If trying to move between pinned and unpinned sections, don't allow it
				if (isMovingPinnedTab !== isMovingToPinnedArea) {
					return prev; // Don't allow mixing pinned and unpinned
				}
			}
			
			// Perform the reorder within the filtered tabs
			const [movedTab] = tabsToReorder.splice(oldIndex, 1);
			tabsToReorder.splice(newIndex, 0, movedTab);
			
			// If panelId is provided, merge back with other panels' tabs
			if (panelId) {
				const otherTabs = prev.tabs.filter(tab => tab.panelId !== panelId);
				return {
					...prev,
					tabs: [...otherTabs, ...tabsToReorder],
				};
			}
			
			return {
				...prev,
				tabs: tabsToReorder,
			};
		});
	}, []);

	const setFocusedTab = useCallback((tabId: string) => {
		setWorkspaceTabsData((prev) => ({
			...prev,
			focusedTabId: tabId,
		}));
	}, []);

	const clearFocusedTab = useCallback(() => {
		setWorkspaceTabsData((prev) => ({
			...prev,
			focusedTabId: null,
		}));
	}, []);

	const setPanelsFocusMode = useCallback((enabled: boolean) => {
		setWorkspaceTabsData((prev) => ({
			...prev,
			panelsFocusMode: enabled,
			// Clear tab focus mode when entering panels focus mode
			focusedTabId: enabled ? null : prev.focusedTabId,
		}));
	}, []);

	const updateTabNameForChannel = useCallback((channelId: string, newName: string) => {
		setWorkspaceTabsData((prev) => {
			// Update tabs in the main tabs array
			const updatedTabs = prev.tabs.map((tab) =>
				tab.channelId === channelId ? { ...tab, name: newName } : tab,
			);

			// Update tabs in recentlyVisited array
			const updatedRecentlyVisited = prev.recentlyVisited.map((entry) =>
				entry.tab.channelId === channelId
					? { ...entry, tab: { ...entry.tab, name: newName } }
					: entry,
			);

			// Update tabs in recentlyClosed array
			const updatedRecentlyClosed = prev.recentlyClosed.map((entry) =>
				entry.tab.channelId === channelId
					? { ...entry, tab: { ...entry.tab, name: newName } }
					: entry,
			);

			return {
				...prev,
				tabs: updatedTabs,
				recentlyVisited: updatedRecentlyVisited,
				recentlyClosed: updatedRecentlyClosed,
			};
		});
	}, []);

	// Track URL changes for navigation history
	// useEffect(() => {
	// 	const currentPath = location.pathname;
	// 	const existingTab = tabs.find((t) => t.path === currentPath);
		
	// 	// If we navigated to an existing tab's path (e.g., via browser back/forward or direct navigation)
	// 	// update it as the active tab without updating history (to avoid loops)
	// 	if (existingTab && existingTab.id !== activeTabId) {
	// 		setActiveTab(existingTab.id, false);
	// 		console.log("[tabs-context.tsx(useEffect)] Calling setActiveTab with ", existingTab.id);
	// 	}
	// }, [location.pathname, tabs, activeTabId, setActiveTab]);

	// Keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Ctrl+W or Cmd+W: Close current tab
			if ((e.ctrlKey || e.metaKey) && e.key === "w") {
				e.preventDefault();
				if (activeTabId) {
					closeTab(activeTabId);
				}
			}

			// Ctrl+Shift+T or Cmd+Shift+T: Restore last closed tab
			if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "T") {
				e.preventDefault();
				if (recentlyClosed.length > 0) {
					restoreTab(recentlyClosed[0].tab);
				}
			}

			// Alt+Left: Navigate back
			if (e.altKey && e.key === "ArrowLeft") {
				e.preventDefault();
				navigateBack();
			}

			// Alt+Right: Navigate forward
			if (e.altKey && e.key === "ArrowRight") {
				e.preventDefault();
				navigateForward();
			}

			// ESC: Exit focus mode (both tab and panels focus mode)
			if (e.key === "Escape" && (focusedTabId || panelsFocusMode)) {
				e.preventDefault();
				if (focusedTabId) {
					clearFocusedTab();
				}
				if (panelsFocusMode) {
					setPanelsFocusMode(false);
				}
			}

			// F11 or Ctrl+Shift+F / Cmd+Shift+F: Toggle tab focus mode
			if (e.key === "F11" || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "F")) {
				e.preventDefault();
				if (activeTabId) {
					if (focusedTabId) {
						clearFocusedTab();
					} else {
						// Exit panels focus mode if active
						if (panelsFocusMode) {
							setPanelsFocusMode(false);
						}
						setFocusedTab(activeTabId);
					}
				}
			}

			// Ctrl+Shift+P / Cmd+Shift+P: Toggle panels focus mode
			if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "P") {
				e.preventDefault();
				setPanelsFocusMode(!panelsFocusMode);
			}

			// Ctrl+Tab or Cmd+Tab: Cycle through tabs
			if ((e.ctrlKey || e.metaKey) && e.key === "Tab") {
				e.preventDefault();
				const currentIndex = tabs.findIndex((t) => t.id === activeTabId);
				const nextIndex = e.shiftKey
					? (currentIndex - 1 + tabs.length) % tabs.length
					: (currentIndex + 1) % tabs.length;

				if (tabs[nextIndex]) {
					setActiveTab(tabs[nextIndex].id);
					navigate({ to: tabs[nextIndex].path as any });
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [
		activeTabId,
		tabs,
		recentlyClosed,
		closeTab,
		restoreTab,
		setActiveTab,
		navigate,
		navigateBack,
		navigateForward,
		focusedTabId,
		clearFocusedTab,
		panelsFocusMode,
		setPanelsFocusMode,
	]);

	return (
		<TabsContext.Provider
			value={{
				tabs,
				activeTabId,
				activeTabIdByPanel,
				temporaryTab,
				recentlyVisited,
				recentlyClosed,
				navigationHistory,
				historyPosition,
				canGoBack,
				canGoForward,
				focusedTabId,
				panelsFocusMode,
				openTab,
				openTemporaryTab,
				openPermanentTab,
				openTabInBackground,
				confirmTab,
				closeTab,
				closeTabs,
				setActiveTab,
				setActiveTabForPanel,
				getActiveTabForPanel,
				togglePinTab,
				restoreTab,
				clearHistory,
				getTabByPath,
				getTabsByPanel,
				navigateBack,
				navigateForward,
				reorderTabs,
				moveTabToPanel,
				moveTabToPanelLastIndex,
				setFocusedTab,
				clearFocusedTab,
				setPanelsFocusMode,
				updateTabNameForChannel,
				// expose channel helpers
				getTabTypeFromChannelType,
				buildChannelPath,
				createTabDataFromChannel,
				setCurrentWorkspace: (workspaceId: string) => {
					if (workspaceId !== currentWorkspaceId) {
						setWorkspaceTabsData(loadWorkspaceTabs(Number(workspaceId)));
					}
				},
			}}
		>
			{children}
		</TabsContext.Provider>
	);
};

export const useTabs = () => {
	const context = useContext(TabsContext);
	if (!context) {
		throw new Error("useTabs must be used within a TabsProvider");
	}
	return context;
};
